import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./ClubMonitor.css";

function ClubMonitor() {
  const [micData, setMicData] = useState([]);
  const [piezoData, setPiezoData] = useState([]);
  const [accelData, setAccelData] = useState({ x: [], y: [], z: [] });
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const micChartRef = useRef(null);
  const piezoChartRef = useRef(null);
  const accelChartRef = useRef(null);

  useEffect(() => {
    const ws = new WebSocket("wss://api.andrewlee.tech/live");
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setError(null);
    };
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (typeof data.mic === "number") {
          setMicData((d) => {
            const arr = [...d, data.mic].slice(-200);
            return arr;
          });
        }
        if (typeof data.piezo === "number") {
          setPiezoData((d) => {
            const arr = [...d, data.piezo].slice(-200);
            return arr;
          });
        }
        if (
          data.accel &&
          typeof data.accel.x === "number" &&
          typeof data.accel.y === "number" &&
          typeof data.accel.z === "number"
        ) {
          setAccelData((prev) => ({
            x: [...prev.x, data.accel.x].slice(-200),
            y: [...prev.y, data.accel.y].slice(-200),
            z: [...prev.z, data.accel.z].slice(-200),
          }));
        }
      } catch (e) {
        console.error(e);
      }
    };
    ws.onclose = () => setIsConnected(false);
    ws.onerror = () => setError("WebSocket error");

    return () => ws.close();
  }, []);

  const drawChart = (canvas, data, color) => {
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width;
      canvas.height = rect.height;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (data.length === 0) return;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();

    data.forEach((v, i) => {
      const x = (i / (data.length - 1)) * canvas.width;
      const y = canvas.height - ((v - min) / range) * canvas.height;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();

    // fill under curve
    ctx.fillStyle = color.replace("rgb", "rgba").replace(")", ",0.1)");
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = (i / (data.length - 1)) * canvas.width;
      const y = canvas.height - ((v - min) / range) * canvas.height;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.lineTo(canvas.width, canvas.height);
    ctx.lineTo(0, canvas.height);
    ctx.closePath();
    ctx.fill();
  };

  const drawAccelChart = (canvas, accelData) => {
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width;
      canvas.height = rect.height;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (accelData.x.length === 0) return;

    const allValues = [...accelData.x, ...accelData.y, ...accelData.z];
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const range = max - min || 1;

    const colors = {
      x: "rgb(255, 100, 100)",
      y: "rgb(100, 255, 100)",
      z: "rgb(100, 100, 255)",
    };

    Object.entries(accelData).forEach(([axis, data]) => {
      if (data.length === 0) return;

      ctx.strokeStyle = colors[axis];
      ctx.lineWidth = 2;
      ctx.beginPath();

      data.forEach((v, i) => {
        const x = (i / (data.length - 1)) * canvas.width;
        const y = canvas.height - ((v - min) / range) * canvas.height;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke();
    });
  };

  useEffect(() => {
    if (micChartRef.current) {
      requestAnimationFrame(() =>
        drawChart(micChartRef.current, micData, "rgb(0,255,136)")
      );
    }
  }, [micData]);

  useEffect(() => {
    if (piezoChartRef.current) {
      requestAnimationFrame(() =>
        drawChart(piezoChartRef.current, piezoData, "rgb(255,100,255)")
      );
    }
  }, [piezoData]);

  useEffect(() => {
    if (accelChartRef.current) {
      requestAnimationFrame(() =>
        drawAccelChart(accelChartRef.current, accelData)
      );
    }
  }, [accelData]);

  return (
    <div className="club-monitor">
      <div className="header">
        <Link to="/" className="back-link">
          ← Back to Portfolio
        </Link>
        <h1>Club Monitor Live</h1>
        <div className="status">
          <span
            className={`status-indicator ${
              isConnected ? "connected" : "disconnected"
            }`}
          >
            {isConnected ? "●" : "○"}
          </span>
          <span className="status-text">
            {isConnected ? "Connected" : "Disconnected"}
          </span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="charts-grid">
        <div className="chart-container">
          <h3 className="chart-title">Piezo Sensor</h3>
          <canvas ref={piezoChartRef} className="live-chart" />
          <div className="chart-info">
            <p>Live Piezo Data ({piezoData.length}/200 points)</p>
            <p>Current: {piezoData.slice(-1)[0] ?? "N/A"}</p>
          </div>
        </div>

        <div className="chart-container">
          <h3 className="chart-title">Microphone</h3>
          <canvas ref={micChartRef} className="live-chart" />
          <div className="chart-info">
            <p>Live Microphone Data ({micData.length}/200 points)</p>
            <p>Current: {micData.slice(-1)[0] ?? "N/A"}</p>
          </div>
        </div>

        <div className="chart-container full-width">
          <h3 className="chart-title">Accelerometer</h3>
          <canvas ref={accelChartRef} className="live-chart" />
          <div className="chart-info">
            <p>Live Accelerometer Data ({accelData.x.length}/200 points)</p>
            <div className="accel-values">
              <span className="accel-x">
                X: {accelData.x.slice(-1)[0] ?? "N/A"}
              </span>
              <span className="accel-y">
                Y: {accelData.y.slice(-1)[0] ?? "N/A"}
              </span>
              <span className="accel-z">
                Z: {accelData.z.slice(-1)[0] ?? "N/A"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClubMonitor;
